import type { QueryClient } from '@tanstack/react-query';

import { loadBulkProcessQueue, mergeBulkTargetRefs, saveBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import {
  activeBulkBatchIdSet,
  coalesceInflightApplicationStates,
  evaluateBulkBatchAfterSync,
  expandProcessingIdsForBulkTargets,
  hydrateEveryBulkTargetForResume,
  hydrateInFlightBatchFromDetail,
  hydrateUnsettledBulkTargetsFromDetail,
  RESUME_HYDRATE_MAX_PER_SYNC,
} from '@/lib/bulkProcessQueueSync';
import { clearBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import { useBulkProcessBannerStore } from '@/stores/bulkProcessBannerStore';
import { resetBulkListRefetchThrottle } from '@/lib/bulkQueuePoll';
import { mergeApplicationsById } from '@/lib/bulkProcessApplications';
import { mergeApplicationListsPreferNewest } from '@/lib/companyApplicationsList';
import { createApplicationListSync } from '@/lib/applicationListSync';
import { persistRecentSettledApplications } from '@/lib/recentSettledApplications';
import type { Application } from '@/types';

export type SyncBulkQueueProgressOptions = {
  targetIds?: readonly string[];
  /** Force a list refetch before hydrate (F5 resume). */
  forceListRefetch?: boolean;
  /** Hydrate all batch targets in one pass after reload. */
  resumeHydrate?: boolean;
};

/** Poll path: refetch list, hydrate batch via detail API, reconcile queue, patch cache + session. */
export async function syncBulkQueueProgress(
  queryClient: QueryClient,
  companyId: string,
  syncBanner: (
    companyId: string,
    processingIds: Set<string>,
    stoppingIds: Set<string>,
    applications?: Application[],
  ) => void,
  options?: SyncBulkQueueProgressOptions,
): Promise<void> {
  const loaded = loadBulkProcessQueue(companyId);
  const targetIds = options?.targetIds?.length
    ? [...options.targetIds]
    : loaded.targetIds;

  const listSync = createApplicationListSync(queryClient);
  let apps = listSync.getFlattenedListApplications(companyId, false);

  const forceRefetch = options?.forceListRefetch === true;

  if (forceRefetch) {
    resetBulkListRefetchThrottle(companyId);
  }

  // Refetch infinite list only when cache is empty (e.g. F5 resume). Never refetch
  // legacy `applications` hydrate — it races patches and resets sort on Applications.
  if (apps.length === 0) {
    await queryClient.refetchQueries({
      queryKey: ['applications-list', companyId],
      type: 'active',
    });
    apps = listSync.getFlattenedListApplications(companyId, false);
  }

  const batchScope = activeBulkBatchIdSet(
    targetIds,
    loaded.processingIds,
    loaded.stoppingIds,
  );
  const scopedProcessing = new Set(
    [...loaded.processingIds].filter((id) => batchScope.has(id)),
  );
  const scopedStopping = new Set(
    [...loaded.stoppingIds].filter((id) => batchScope.has(id)),
  );

  const hydrateMax = options?.resumeHydrate
    ? Math.min(RESUME_HYDRATE_MAX_PER_SYNC, Math.max(targetIds.length, 1))
    : undefined;

  // List API often lags — detail endpoint matches Application detail page truth.
  if (targetIds.length > 0 && options?.resumeHydrate) {
    apps = await hydrateEveryBulkTargetForResume(companyId, apps, targetIds, {
      maxPerSync: hydrateMax ?? RESUME_HYDRATE_MAX_PER_SYNC,
      targetRefs: loaded.targetRefs,
    });
  } else if (loaded.targetIds.length > 0) {
    apps = await hydrateUnsettledBulkTargetsFromDetail(
      companyId,
      apps,
      targetIds,
      {
        maxPerSync: hydrateMax !== undefined ? hydrateMax : undefined,
        targetRefs: loaded.targetRefs,
      },
    );
  } else if (scopedProcessing.size > 0) {
    apps = await hydrateInFlightBatchFromDetail(
      companyId,
      apps,
      loaded.targetIds,
      scopedProcessing,
      loaded.targetRefs,
    );
  }

  const outcome = evaluateBulkBatchAfterSync(targetIds, apps, {
    stoppingIds: scopedStopping,
    processingIds: scopedProcessing,
    completed: loaded.completed,
  });

  if (outcome.action === 'clear') {
    clearBulkProcessQueue(companyId);
    useBulkProcessBannerStore.getState().clearBulkRun();
    listSync.patchCompanyUpdater(companyId, (prev) => mergeApplicationListsPreferNewest(prev, apps), false);
    const settled = apps.filter((a) => targetIds.includes(a.id));
    if (settled.length > 0) {
      persistRecentSettledApplications(companyId, settled);
      listSync.patchMany(companyId, settled, false);
    }
    return;
  }

  const { processingIds: expandedProcessing, stoppingIds, completed } = outcome;
  const mergedTargetRefs = mergeBulkTargetRefs(loaded.targetRefs, apps, targetIds);
  saveBulkProcessQueue(
    companyId,
    stoppingIds,
    expandedProcessing,
    targetIds,
    completed,
    mergedTargetRefs,
  );
  listSync.patchCompanyUpdater(companyId, (prev) => {
    const merged = mergeApplicationListsPreferNewest(prev, apps);
    if (expandedProcessing.size === 0) {
      return merged;
    }
    return coalesceInflightApplicationStates(merged, expandedProcessing);
  }, false);
  syncBanner(companyId, expandedProcessing, stoppingIds, apps);
}
